import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { FiEdit, FiTrash } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';
import ResponsiveDataList from '../components/common/ResponsiveDataList';
import MobileCard from '../components/common/MobileCard';

const emptyBranche = { lib_branche: '' };

const BranchesPage = () => {
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBranche);

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    const res = await api.get('/branches');
    setBranches(res.data.data || []);
  };

  const handleAdd = () => {
    setEditing(null);
    setForm(emptyBranche);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({ lib_branche: item.lib_branche });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette branche ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      await api.delete(`/branches/${id}`);
      fetchBranches();
      Swal.fire('Supprimé !', 'La branche a été supprimée.', 'success');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyBranche);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.lib_branche) {
      await Swal.fire({ icon: 'error', title: 'Erreur', text: 'Le nom de la branche est obligatoire.' });
      return;
    }
    if (editing) {
      await api.put(`/branches/${editing.id}`, form);
    } else {
      await api.post('/branches', form);
    }
    fetchBranches();
    setShowModal(false);
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Branches</h1>
        <ConditionalButton
          entity="branches"
          permission="create"
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          onClick={handleAdd}
        >
          Nouvelle branche
        </ConditionalButton>
      </div>
      <ResponsiveDataList
        isLoading={false}
        isEmpty={branches.length === 0}
        emptyMessage="Aucune branche"
        mobileCards={branches.map((item) => (
          <MobileCard
            key={item.id}
            title={item.lib_branche}
            actions={
              <ConditionalActions
                entity="branches"
                onEdit={() => handleEdit(item)}
                onDelete={() => handleDelete(item.id)}
                editIcon={FiEdit}
                deleteIcon={FiTrash}
                className="flex space-x-2"
              />
            }
          />
        ))}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Libellé</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {branches.length === 0 ? (
              <tr>
                <td colSpan="2" className="px-6 py-4 text-center text-gray-500">Aucune branche</td>
              </tr>
            ) : (
              branches.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4">{item.lib_branche}</td>
                  <td className="px-6 py-4 text-right">
                    <ConditionalActions
                      entity="branches"
                      onEdit={() => handleEdit(item)}
                      onDelete={() => handleDelete(item.id)}
                      editIcon={FiEdit}
                      deleteIcon={FiTrash}
                      className="flex space-x-2"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ResponsiveDataList>
      <Modal
        isOpen={showModal}
        onRequestClose={handleModalClose}
        contentLabel="Branche"
        ariaHideApp={false}
        style={{
          content: {
            maxWidth: '400px',
            width: '100%',
            margin: 'auto',
            height: '200px',
            borderRadius: 12,
            overflow: 'hidden',
            padding: 0
          }
        }}
      >
        <form onSubmit={handleSubmit} className="p-6">
          <h2 className="text-lg font-semibold mb-4">{editing ? 'Modifier' : 'Nouvelle'} branche</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Libellé *</label>
            <input
              type="text"
              name="lib_branche"
              value={form.lib_branche}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={handleModalClose} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300">Annuler</button>
            <ConditionalButton
              entity="branches"
              permission={editing ? "update" : "create"}
              type="submit"
              className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
            >
              {editing ? 'Enregistrer' : 'Créer'}
            </ConditionalButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default BranchesPage;
